import React, { useCallback, useMemo, useState } from 'react';
import { useCan, useOrganizationId } from '@features/user';
import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Typography } from '@mui/material';
import { useEventTracking } from '@services/statsig';

import { useOrganizationAppointmentTypes } from '../../store/hooks';
import type { AppointmentType } from '../../types';
import AppointmentTypeModal from '../AppointmentTypeModal';

import {
  LinkAppointmentTypeModal,
  type TemplateTypeLinkingSource,
} from './LinkAppointmentTypeModal';
import { LinkedTypeChips } from './LinkedTypeChips';

export interface TemplateLinkedTypesSectionProps {
  /** The note template whose linked appointment types are shown. */
  templateId: string;
  /** Display name of the template, used in copy and create-mode seeding. */
  templateName?: string;
  /** 'full' for the template editor; 'compact' for the AI-builder completion screen. */
  variant?: 'full' | 'compact';
  source: TemplateTypeLinkingSource;
}

/**
 * Shows and manages which appointment types use a note template (GDN-2149).
 * Renders nothing without the manage_appointment_types permission — all
 * appointment types are org-scoped, so the linking controls are org-admin
 * territory.
 */
export const TemplateLinkedTypesSection: React.FC<TemplateLinkedTypesSectionProps> = ({
  templateId,
  templateName,
  variant = 'full',
  source,
}) => {
  const canManageAppointmentTypes = useCan('manage_appointment_types');
  const organizationId = useOrganizationId();
  const { template } = useEventTracking();

  const { appointmentTypes, isLoading, error } = useOrganizationAppointmentTypes(
    organizationId,
    undefined,
    { skip: !canManageAppointmentTypes },
  );

  const linkedTypes = useMemo(
    () => appointmentTypes.filter(type => type.template_id === templateId),
    [appointmentTypes, templateId],
  );

  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [editType, setEditType] = useState<AppointmentType | null>(null);

  const handleOpenLinkModal = useCallback(() => {
    template.appointmentTypeLinkOpened({
      template_id: templateId,
      source,
      linked_count: linkedTypes.length,
    });
    setIsLinkModalOpen(true);
  }, [template, templateId, source, linkedTypes.length]);

  if (!canManageAppointmentTypes || error || isLoading) {
    return null;
  }

  const modals = (
    <>
      <LinkAppointmentTypeModal
        open={isLinkModalOpen}
        onClose={() => setIsLinkModalOpen(false)}
        templateId={templateId}
        templateName={templateName}
        source={source}
      />
      <AppointmentTypeModal
        open={editType !== null}
        onClose={() => setEditType(null)}
        type="edit"
        appointmentType={editType}
        existingAppointmentTypes={appointmentTypes}
      />
    </>
  );

  if (variant === 'compact') {
    return (
      <Box sx={{ textAlign: 'left' }}>
        {linkedTypes.length > 0 ? (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Appointment types using this template
            </Typography>
            <LinkedTypeChips
              size="small"
              linkedTypes={linkedTypes}
              onTypeClick={setEditType}
              onLinkClick={handleOpenLinkModal}
            />
          </>
        ) : (
          <>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Put this template to work
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Link an appointment type so visits of that type automatically chart with this
              template.
            </Typography>
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleOpenLinkModal}
            >
              Link an appointment type
            </Button>
          </>
        )}
        {modals}
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
        Appointment types
      </Typography>
      {linkedTypes.length > 0 ? (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Appointments of these types automatically chart with this template.
          </Typography>
          <LinkedTypeChips
            linkedTypes={linkedTypes}
            onTypeClick={setEditType}
            onLinkClick={handleOpenLinkModal}
          />
        </>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            No appointment types use this template yet, so it won&apos;t be applied automatically
            during charting.
          </Typography>
          <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={handleOpenLinkModal}>
            Link an appointment type
          </Button>
        </>
      )}
      {modals}
    </Box>
  );
};
